import { useEffect, useState, useRef } from "react";
import { Quote } from "lucide-react";

const QUOTES = [
  {
    body: "Most candidates have no idea how aggressive the ATS filter is. 75-80% of resumes are auto-rejected before a human ever sees them. Tools like this are the only way to know what you're actually shipping to the recruiter.",
    name: "Maya R.",
    role: "Sr. Tech Recruiter",
    company: "ex-Stripe, Square",
  },
  {
    body: "I scan a resume in 7 seconds. If you don't have quantified impact in the top third, you're out. The scoring breakdown here mirrors how I read.",
    name: "Daniel O.",
    role: "Engineering Manager",
    company: "Series B SaaS",
  },
  {
    body: "We hired 38 PMs last year. The ones who landed offers had resumes that read like ResumeLab's 'after' examples — specific scope, real numbers, tight bullets.",
    name: "Priya S.",
    role: "Head of Talent",
    company: "Fintech, 800 FTE",
  },
  {
    body: "Candidates think a clean design helps. It doesn't. ATS parsers strip formatting. What survives is your verbs and your metrics. Everything else is noise.",
    name: "Jordan T.",
    role: "Lead Recruiter",
    company: "FAANG, 12 yrs",
  },
];

export default function RecruiterQuotes() {
  const [idx, setIdx] = useState(0);
  const containerRef = useRef(null);
  const paused = useRef(false);

  useEffect(() => {
    const t = setInterval(() => {
      if (!paused.current) setIdx((i) => (i + 1) % QUOTES.length);
    }, 11000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      className="border-b border-zinc-200 bg-zinc-50"
      data-testid="recruiter-quotes"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      ref={containerRef}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="overline mb-6">▲ WHAT RECRUITERS ACTUALLY SAY</div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 relative min-h-[280px]">
            {QUOTES.map((q, i) => (
              <blockquote
                key={i}
                data-testid={`quote-${i}`}
                aria-hidden={i !== idx}
                className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                <Quote className="w-8 h-8 text-[#DC1C22] mb-6" strokeWidth={1.5} />
                <p className="font-display font-bold text-2xl lg:text-3xl tracking-tight leading-[1.2] text-zinc-900 mb-8">
                  "{q.body}"
                </p>
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 bg-zinc-900 text-white inline-flex items-center justify-center font-display font-bold">
                    {q.name.charAt(0)}
                  </span>
                  <div>
                    <div className="font-medium text-zinc-900">{q.name}</div>
                    <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">{q.role} · {q.company}</div>
                  </div>
                </div>
              </blockquote>
            ))}
          </div>

          {/* Dot navigation */}
          <div className="lg:col-span-4 lg:col-start-9 flex lg:flex-col gap-2 lg:gap-3 lg:justify-end items-start">
            {QUOTES.map((q, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`quote-dot-${i}`}
                aria-label={`Show quote from ${q.name}`}
                className={`text-left transition-all w-full ${
                  i === idx ? "opacity-100" : "opacity-50 hover:opacity-80"
                }`}
              >
                <div className={`h-px w-full mb-2 ${i === idx ? "bg-[#DC1C22]" : "bg-zinc-300"}`} />
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 truncate">
                  {q.name.split(" ")[0]} · {q.role}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
